import { computed, type IObservableValue, observable, observe, reaction, runInAction } from "mobx"
import { createContext } from "react"
import { toast } from "sonner"

import { ChatModelImpl } from "@/components/Chat/ChatModel.ts"
import { createCompletionProvider } from "@/components/PromptInput/Completion/completionProviders.ts"
import { PromptInputModelImpl } from "@/components/PromptInput/PromptInputModel.ts"
import { withGlobalLoader } from "@/lib/context.ts"
import { Lifetime } from "@/lib/Lifetime.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { projectQueryKey } from "@/lib/queries.ts"
import { isUserPrincipal, parseTaskSettings, successfulAttachments } from "@/lib/std.ts"
import { removeTokenParams } from "@/lib/url.ts"
import { validateEmail } from "@/lib/validateUtils.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { projectPermissionsProp } from "@/routes/projects/projectHooks.ts"
import { taskDetailsKey, taskKey, tasksByRepoKey, tasksByUserKey } from "@/routes/tasks/tasksHooks.ts"
import { TaskViewId } from "@/routes/tasks/TaskViewId.ts"
import { generateStructuredSummary } from "@/routes/tasks/TaskViewScene/generateStructuredSummary.ts"
import { GitHubModel } from "@/routes/tasks/TaskViewScene/GitHubModel.ts"
import { TaskChangesFromPreviousVersionModel } from "@/routes/tasks/TaskViewScene/TaskChangesFromPreviousVersionModel.ts"
import { TaskChangesModel } from "@/routes/tasks/TaskViewScene/TaskChangesModel.ts"
import { allScriptsStarted, TaskPreviewModel } from "@/routes/tasks/TaskViewScene/TaskPreviewModel.ts"
import { isTabId, TabId } from "@/routes/tasks/TaskViewScene/TaskTabId.ts"

import type { ChatModel } from "@/components/Chat/ChatModel.ts"
import type { DiffFile, DiffViewModel } from "@/components/DiffView/DiffView.tsx"
import type {
  InviteDeveloperHint,
  PromptInputInitialState,
  PromptInputModel,
  PromptInputModeType,
  PromptInputSubmitArgs
} from "@/components/PromptInput/PromptInputModel.ts"
import type { RouterType } from "@/context.ts"
import type {
  AiAgentTask,
  AiAgentTaskContextItem,
  AiAgentTaskDetails,
  AiAgentTaskEvent,
  AiAgentTaskHistoryItem,
  CPrincipal,
  DevTaskStats,
  GrazieLLMProfileDTO,
  PR_Project2,
  ProjectCreationBreadcrumb,
  ProjectKey,
  RepositoryFetchStatus,
  RuntimeConfig,
  TD_MemberProfile,
  TID
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { Queries } from "@/lib/queries.ts"
import type { ProjectPermissionsProp } from "@/routes/projects/projectHooks.ts"
import type { TaskPreviewTabModel } from "@/routes/tasks/TaskViewScene/TaskPreviewTabModel.tsx"
import type { TaskTabId } from "@/routes/tasks/TaskViewScene/TaskTabId.ts"
import type { Observable } from "rxjs"

const log = getLogger("TaskViewModel")

const CHAT_PERCENTAGE_STORAGE_KEY = "chat-percentage"
const TABS_PERCENTAGE_STORAGE_KEY = "tabs-percentage"

export class TaskViewModel extends Model {
  private readonly router: RouterType
  private readonly context: OrgContext
  private readonly queries: Queries
  private readonly updateQueue: TaskViewUpdateRequest[] = []
  private readonly devMode: boolean
  private readonly createdByUser: TD_MemberProfile | null

  private lazyChangesModel: DiffViewModel | null = null
  private lazyChangesFromPreviousVersionModel: DiffViewModel | null = null

  private updates: Observable<AiAgentTaskEvent> | null = null
  private isProcessing = false

  readonly chatModel: ChatModel
  readonly inputModel: PromptInputModel
  readonly completingPreviewRuntimeSetup = observable.box<boolean>(false)

  readonly project: IObservableValue<PR_Project2>
  readonly taskId: string
  readonly orgId: string

  readonly task = observable.box<AiAgentTask | null>(null)
  readonly stats = observable.box<Omit<DevTaskStats, "taskId">>({ parsingErrorsCount: 0, failedSearchReplaceCount: 0 })

  readonly me = observable.box<CPrincipal | null>(null)

  readonly llmProfiles = observable.box<GrazieLLMProfileDTO[]>([])

  readonly chatMode = computed(() => this.task.get()?.chatMode)

  readonly projectName = observable.box<string | undefined>(undefined)

  readonly successful = computed(() => this.task.get()?.successful)

  readonly readonly = computed(() => this.task.get()?.readonly === true)

  readonly agentIsRunning = computed(() => this.task.get()?.running === true)

  readonly initialRef = computed(() => this.task.get()?.initialRef ?? null)

  readonly showChatInput = computed(() => !this.isFinishedConfigurationTask?.get())

  readonly historyItems = observable.box<AiAgentTaskHistoryItem[]>([])

  readonly currentVersionCommitId = computed(() => {
    const items = this.historyItems.get().filter((item) => item.isActive)
    return items.length > 0 ? items[items.length - 1].commit.id : null
  })

  readonly successfulVersionCommitIds = computed(() => {
    return new Set(
      this.historyItems.get()
        .filter((item) => item.isSuccessful)
        .map((item) => item.commit.id)
    )
  })

  readonly airDeepLink = computed(() => {
    const params = new URLSearchParams({
      ship_type: "DarkMatter",
      project: this.project.get().key.key,
      repository: this.repositoryName,
      taskId: this.taskId,
      spaceUrl: "https://jetbrains.team",
    })

    return `https://fleet.labs.jb.gg?${params.toString()}`
  })

  readonly gitHubModel: GitHubModel

  readonly currentTab = observable.box<TaskTabId>(TabId.Preview)
  readonly currentView = observable.box<TaskViewId>(TaskViewId.Full)

  readonly chatPercentage = observable.box(40)
  readonly tabsPercentage = observable.box(60)

  readonly guidelinesReloadSignal = observable.box(0)

  readonly totalErrorsCount = computed(() => {
    const { parsingErrorsCount, failedSearchReplaceCount } = this.stats.get()
    return parsingErrorsCount + failedSearchReplaceCount
  })

  readonly initProjectStep = computed(() => this.task.get()?.initProjectState?.step ?? null)

  readonly isConfigurationTask = computed(() => this.task.get()?.initRuntimeConfigState != null || this.task.get()?.initProjectState != null)

  readonly isFinishedConfigurationTask = computed(() =>
    this.isConfigurationTask.get() &&
    this.task.get()?.readonly === true &&
    this.task.get()?.successful === true &&
    !this.completingPreviewRuntimeSetup.get()
  )

  readonly showPreviewLooksGoodButton = computed(() =>
    this.isConfigurationTask.get() &&
    !this.isFinishedConfigurationTask.get() &&
    allScriptsStarted(this.previewProcessesState.get()) &&
    this.task.get()?.initRuntimeConfigState?.previewSetupSucceeded === true
  )

  readonly configurationTaskBreadcrumbs = computed(() => {
    const step = this.initProjectStep.get()
    const configState = this.task.get()?.initRuntimeConfigState
    if (step === null && configState === null) {
      return null
    }
    const uiBreadcrumbs = ["Repository", "Services", "Preview"] as const

    const serverToUiMapping: Record<ProjectCreationBreadcrumb, typeof uiBreadcrumbs[number]> = {
      Repository: "Repository",
      Services: "Services", // merged
      Secrets: "Services", // merged
      Preview: "Preview"
    }
    const activeBreadcrumb: typeof uiBreadcrumbs[number] = serverToUiMapping[configState?.breadcrumb ?? "Repository"]

    return uiBreadcrumbs.map(it => ({
      name: it,
      active: it === activeBreadcrumb
    }))
  })

  readonly inviteDeveloperHint = computed<InviteDeveloperHint | null>(() => {
    const step = this.initProjectStep.get()
    const configState = this.task.get()?.initRuntimeConfigState
    const taskCreatedByMe = this.task.get()?.createdBy.id === this.me.get()?.id

    if (step === null && configState === null || !taskCreatedByMe) {
      return null
    }

    const servicesCount = configState?.services?.length ?? 0
    const stuckWithServices = configState?.breadcrumb === "Services" && servicesCount >= 3
    if (stuckWithServices) {
      return {
        id: "services",
        title: "Not sure how to proceed?",
        callToAction: "Invite your teammate to help finish setup."
      }
    }

    const stuckWithSecrets = configState?.breadcrumb === "Secrets" && this.chatModel.messages.get().some(it => it.content.className === "SecretList")
    if (stuckWithSecrets) {
      return {
        id: "secrets",
        title: "Don't know the secrets?",
        callToAction: "Invite your teammate who knows how to get the secret values."
      }
    }

    return null
  })

  readonly inputSuggestedState: IObservableValue<PromptInputInitialState | null> = computed(() => {
    const initProjectState = this.task.get()?.initProjectState
    if (!initProjectState) {
      return null
    }
    if (initProjectState.step === "EnterProjectName" && initProjectState.suggestedName) {
      return {
        prompt: initProjectState.suggestedName,
        domElements: [],
        context: [],
        attachments: []
      } satisfies PromptInputInitialState
    }
    return null
  })

  readonly runtimeConfig = observable.box<RuntimeConfig | null>(null)

  private permissions: ProjectPermissionsProp | null
  readonly repoSyncStatus = observable.box<RepositoryFetchStatus | null>(null)

  /** null - loading, false - no preview, string - previewId */
  readonly previewId = observable.box<null | false | string>(null)
  readonly previewV2Ids = observable.box<null | false | string[]>(null)
  readonly previewV2EnvId = observable.box<null | false | string>(null)

  readonly isMultiPreview = computed(() => {
    const previewV2Ids = this.previewV2Ids.get()
    console.log(`TaskViewModel:isMultiPreview recalculated (previewV2Ids=${previewV2Ids})`)
    return Array.isArray(previewV2Ids) && previewV2Ids.length > 0
  })

  // Single-preview (v1)
  private previewModel: TaskPreviewModel | null | false = null

  // Multi-preview (v2)
  readonly previewModelsById = observable.map<string, TaskPreviewModel>()

  readonly currentPreviewId = observable.box<string | null>(null)
  readonly currentPreview = observable.box<TaskPreviewModel | null | false>(null)

  readonly previewTabModels = observable.map<string, TaskPreviewTabModel>()
  readonly previewTabModel = observable.box<TaskPreviewTabModel | null>(null)

  readonly previewProcessesState = computed(() => {
    return (this.currentPreview.get() || null)?.previewProcessesState?.get() || null
  })

  readonly taskSettings = computed(() => {
    const task = this.task.get()
    if (!task) {
      return null
    }

    return parseTaskSettings(task)
  })

  constructor(params: {
    readonly context: OrgContext
    readonly queries: Queries
    readonly router: RouterType
    readonly orgId: string
    readonly project: PR_Project2
    readonly taskId: string
    readonly tab?: TaskTabId
    readonly view?: TaskViewId
    readonly message?: string,
    readonly taskWithDetails?: AiAgentTaskDetails | null
    readonly updates?: Observable<AiAgentTaskEvent> | null
    readonly devMode: boolean
  }) {
    super("TaskViewModel")

    this.router = params.router
    this.context = params.context
    this.queries = params.queries
    this.devMode = params.devMode

    this.orgId = params.orgId
    this.project = observable.box<PR_Project2>(params.project)
    this.taskId = params.taskId

    runInAction(() => {
      console.log(`TaskViewModel:constructor - setting previewId to ${params.taskWithDetails?.previewId ?? false}`)
      this.previewId.set(params.taskWithDetails?.previewId ?? false)
    })

    this.lifetime.add(reaction(
      () => ({
        isMulti: this.isMultiPreview.get(),
        currentPreviewId: this.currentPreviewId.get(),
      }),
      ({ isMulti, currentPreviewId }) => {

        if (isMulti) {
          if (!currentPreviewId) {
            this.currentPreview.set(null)
          } else {
            const currentPreviewModel = this.previewModelsById.get(currentPreviewId)
            this.currentPreview.set(currentPreviewModel || null)
          }
        } else {
          this.currentPreview.set(this.previewModel)
        }
      }
    ), "TaskViewModel:currentPreview")


    const completionProvider = createCompletionProvider(this.queries, {
      projectKey: this.project.get().key,
      repo: this.repositoryName,
      commit: null
    }, this.context)

    this.chatModel = new ChatModelImpl({
      router: this.router,
      queries: this.queries,
      context: this.context,
      taskId: this.taskId,
      messageId: params.message,
      completionProvider,
      readOnly: computed(() => this.task.get()?.readonly === true),
      devMode: this.devMode
    })

    this.inputModel = new PromptInputModelImpl({
      name: "TaskViewPromptInputModel",
      submit: this.submit,
      taskId: this.taskId,
      completionProvider,
      orgContext: this.context,
      initProjectStep: this.initProjectStep,
      inviteDeveloperHint: this.inviteDeveloperHint,
      canInviteDeveloper: computed(() => this.isConfigurationTask.get()),
      canRequestSupport: computed(() => this.isConfigurationTask.get()),
      liveUpdateDebugEvents: this.chatModel.liveUpdateDebugEvents,
      initialState: this.inputSuggestedState.get() ?? undefined,
      persistable: computed(() => this.isConfigurationTask.get()),
      router: this.router,
    })

    this.gitHubModel = new GitHubModel(new Lifetime(this.lifetime, "TaskViewModel:gitHubModel"), this.context, this.queries, this.taskId, this.task, this.isConfigurationTask, this.project.get(), this.repositoryName, this.historyItems)

    this.lifetime.add(reaction(() => this.inputSuggestedState.get(), (current, prev) => {
      if (!prev && current && !this.inputModel.canSubmit.get()) {
        runInAction(() => {
          this.inputModel.firstInputValue.set(current.prompt)
          this.inputModel.attachments.set(current.attachments.map((it) => Promise.resolve(it)))
          this.inputModel.context.set(current.context)
          if (current.domElements.length > 0) {
            log.error("Not supported: DOM elements in suggested input state")
          }
          this.inputModel.elements.set([])
        })
      }
    }), "TaskViewModel:inputSuggestedState")

    runInAction(() => {
      if (params.taskWithDetails) {
        void this.updateTask(params.taskWithDetails, params.tab === "changes")
      }

      params.tab && this.currentTab.set(params.tab)
      params.view && this.currentView.set(params.view)

      const chatPercentage = this.context.persistence.getItem(CHAT_PERCENTAGE_STORAGE_KEY)
      const tabsPercentage = this.context.persistence.getItem(TABS_PERCENTAGE_STORAGE_KEY)

      if (chatPercentage && tabsPercentage) {
        this.chatPercentage.set(parseFloat(chatPercentage))
        this.tabsPercentage.set(parseFloat(tabsPercentage))
      }
    })

    this.lifetime.add(observe(this.agentIsRunning, change => {
      if (change.oldValue && !change.newValue) {
        setTimeout(() => {
          this.inputModel.inputRef.current?.focus()
        }, 100)
      }
    }), "TaskViewModel:agentIsRunning")

    this.lifetime.add(observe(this.currentTab, change => {
      if (change.newValue === TabId.Changes) {
        void this.changesModel.loadChanges(true)
        void this.changesFromPreviousVersionModel.loadChanges(true)
      }
    }), "TaskViewModel:currentTab")

    if (!this.task.get()) {
      this.scheduleUpdate("refresh")
    }

    this.permissions = null

    void this.listenForUpdates()
    void this.startQueueProcessing()
    void this.subscribeToTaskStats()

    void this.getCurrentPrincipal()

    this.lifetime.add(
      observe(this.isConfigurationTask, change => {
        if (this.task.get() != null && !change.newValue && this.repoSyncStatus.get() == null) {
          void this.subscribeToRepoSyncStatus()
        }
      }, true), "repoSyncErrorsSubscriptions"
    )

    this.lifetime.add(reaction(
      () => ({
        isMulti: this.isMultiPreview.get(),
        previewId: this.previewId.get(),
        previewV2Ids: this.previewV2Ids.get(),
        previewV2EnvId: this.previewV2EnvId.get(),
        initialRef: this.initialRef.get(),
      }),
      ({ isMulti, previewId, previewV2Ids, previewV2EnvId, initialRef }) => {
        console.log(`TaskViewModel:previewModelCreation (isMulti=${isMulti}, previewId=${previewId}, previewV2Ids=${previewV2Ids}, previewV2EnvId=${previewV2EnvId}, initialRef=${initialRef})`)

        // v2: multiple previews

        if (isMulti && Array.isArray(previewV2Ids)) {
          console.log("TaskViewModel:previewModelCreation - multiple previews")
          console.log(previewV2Ids)
          const existingIds = new Set(this.previewModelsById.keys())
          previewV2Ids.forEach((idV2, _) => {
            const model = this.previewModelsById.get(idV2)
            if (model) {
              console.log(`TaskViewModel:previewModelCreation - updating existing preview model ${idV2}`)
              model.idV2.set(idV2)
              model.historyItems.set(this.historyItems.get())
              model.taskInitialCommitHash.set(initialRef)
            } else {
              console.log(`TaskViewModel:previewModelCreation - creating new preview model ${idV2}`)
              const m = new TaskPreviewModel(
                new Lifetime(this.lifetime, `TaskViewModel:previewModel:${idV2}`),
                observable.box(null),
                observable.box(idV2),
                observable.box(previewV2EnvId || null),
                this.taskId,
                this.historyItems,
                observable.box(initialRef),
                this.queries,
                this.task,
                this.runtimeConfig,
                !this.isFinishedConfigurationTask.get(),
                true
              )
              this.previewModelsById.set(idV2, m)
            }
            existingIds.delete(idV2)
          })
          // terminate removed
          existingIds.forEach(id => {
            const m = this.previewModelsById.get(id)
            m?.lifetime.terminate()
            this.previewModelsById.delete(id)
          })

          this.currentPreviewId.set(Array.from(this.previewModelsById.keys())[0] ?? null)

          return
        }

        // v1: single preview
        if (previewId === null || previewId === false) {
          console.log("TaskViewModel:previewModelCreation - previewId is null or false")
          return previewId
        }

        if (this.previewModel) {
          console.log("TaskViewModel:previewModelCreation - preview model exists")
          this.previewModel.id.set(previewId)
          this.previewModel.historyItems.set(this.historyItems.get())
          this.previewModel.taskInitialCommitHash.set(initialRef)
          return previewId
        }

        console.log("TaskViewModel:previewModelCreation - creating single preview model")
        this.previewModel = new TaskPreviewModel(
          new Lifetime(this.lifetime, "TaskViewModel:previewModel"),
          observable.box(previewId),
          observable.box(null),
          observable.box(null),
          this.taskId,
          this.historyItems,
          observable.box(initialRef),
          this.queries,
          this.task,
          this.runtimeConfig,
          !this.isFinishedConfigurationTask.get(),
          false
        )

        this.currentPreviewId.set(previewId || null)
      },
      {
        fireImmediately: true
      }
    ), "TaskViewModel:previewId")

    this.lifetime.add(reaction(
      () => ({
        task: this.task.get(),
        chatReady: this.chatModel.isReady.get(),
      }),
      ({ task, chatReady }) => {
        this.isLoading.set(task === null || !chatReady)
      }
    ), "TaskViewModel:isLoading")

    this.lifetime.add(reaction(
      () => this.task.get()?.projectName,
      name => {
        if (name !== undefined) {
          this.projectName.set(name)
        }
      },
      { fireImmediately: true }
    ), "TaskViewModel:projectName")

    this.lifetime.add(reaction(
      () => this.task.get()?.projectKey,
      async (projectKey) => {
        if (projectKey && projectKey.key !== this.project.get().key.key) {
          const updatedProject = await this.queries.getProjectV2(projectKey)
          runInAction(() => {
            this.project.set(updatedProject)
          })
        }
      },
      { fireImmediately: true }
    ), "TaskViewModel:project")

    const createdBy = this.task.get()?.createdBy
    this.createdByUser = isUserPrincipal(createdBy) ? createdBy.details.user : null

    this.lifetime.add(reaction(
      () => this.currentPreviewId.get(),
      (id) => {
        const model = id ? this.previewTabModels.get(id) ?? null : null
        this.previewTabModel.set(model)
      },
      { fireImmediately: true }
    ), "TaskViewModel:previewTabModelSync")

    void this.loadLlmProfiles()
  }

  private async loadLlmProfiles() {
    try {
      const profiles = await this.queries.getLlmProfileIds()
      runInAction(() => {
        this.llmProfiles.set(profiles)
      })
    } catch (e) {
      log.error("Failed to load LLM profiles:", e)
    }
  }

  readonly selectedLlmProfile = computed(() => {
    const task = this.task.get()
    if (!task) return null

    const profiles = this.llmProfiles.get()
    if (profiles.length === 0) return null

    const llmId = this.taskSettings.get()?.llmProfile

    if (llmId) {
      const profile = profiles.find(profile => profile.id === llmId)
      if (profile) return profile
    }

    return profiles.find(profile => profile.default === true) ?? null
  })

  readonly currentContextSize = computed(() => {
    const task = this.task.get()
    return task?.tokenStats?.inputTokens ?? 0
  })

  readonly totalContextLimit = computed(() => {
    const profile = this.selectedLlmProfile.get()
    return profile?.contextLimit ?? null
  })

  readonly contextLoad = computed(() => {
    const currentSize = this.currentContextSize.get()
    const limit = this.totalContextLimit.get()

    if (limit === null || limit === 0) {
      return null
    }

    const load = currentSize / limit
    return Math.min(Math.max(load, 0.0), 1.0)
  })

  terminate() {
    super.terminate()
    this.chatModel.terminate()
    this.previewTabModels.forEach((model) => {
      model?.terminate()
    })
  }

  readonly updateProjectCoverWithPreviewScreenshot = async () => {
    try {
      const currentPreviewId = this.currentPreviewId.get()
      if (!currentPreviewId) return null
      const currentTabModel = this.previewTabModels.get(currentPreviewId)
      const screenshot = await currentTabModel?.client?.takeScreenshot() ?? null
      if (!screenshot) {
        log.error("Failed to take preview screenshot")
        return
      }

      const response = await fetch(screenshot)
      if (!response.ok) {
        log.error(`Failed to fetch screenshot: ${response.statusText}`)
        return
      }

      const blob = await response.blob()

      const file = new File([blob], "cover.png", { type: "image/png" })
      const attachment = await withGlobalLoader(this.context, this.queries.apiClient.uploadFile(file))
      await withGlobalLoader(this.context, this.queries.apiClient.updateProjectCover(this.project.get().key, attachment.id))

      void this.context.queryClient.invalidateQueries(projectQueryKey(this.project.get().key))
      toast.success("Project cover updated successfully")
    } catch (error) {
      toast.error(`Failed to set project cover: ${error instanceof Error ? error.message : String(error)}`)
      throw error
    }
  }

  private submit = async (args: PromptInputSubmitArgs) => {
    return await this.submitFunctionByInputMode[args.inputMode.type](args)
  }

  private invalidateTaskList = () => {
    void this.context.queryClient.invalidateQueries(tasksByRepoKey(this.project.get().key))

    if (this.createdByUser) {
      void this.context.queryClient.invalidateQueries(tasksByUserKey(this.createdByUser.id))
    }
  }

  private submitFunctionByInputMode: Record<PromptInputModeType, (args: PromptInputSubmitArgs) => Promise<void>> = {
    "Chat": async (args) => {
      const resolvedAttachments = await successfulAttachments(args.attachments)

      this.invalidateTaskList()

      const task = this.task.get()

      if (task) {
        const currentSettings = parseTaskSettings(task)
        const currentLlmId = currentSettings.llmProfile
        const newLlmId = args.llmId ?? null

        if (currentLlmId != newLlmId) {
          const newSettings = { ...currentSettings, llmProfile: newLlmId }

          await this.queries.apiClient.updateTaskSettings(this.taskId, newSettings)
        }
      }

      return this.chatModel.sendMessage(args.firstInputValue, args.context, resolvedAttachments, args.elements)
    },
    "BasicAuth": async (args) => {
      return await this.context.queries.apiClient.setRepositoryAuth(this.taskId, {
        className: "Basic",
        username: args.firstInputValue,
        password: args.secondInputValue
      })
    },
    "SshKey": async (args) => {
      return await this.context.queries.apiClient.setRepositoryAuth(this.taskId, {
        className: "SSH",
        privateKey: args.firstInputValue,
        passphrase: args.secondInputValue
      })
    },
    "Token": async (args) => {
      return await this.context.queries.apiClient.setRepositoryAuth(this.taskId, {
        className: "Token",
        token: args.firstInputValue
      })
    },
    "ProjectName": async (args) => {
      return await this.context.queries.apiClient.setProjectName(this.taskId, args.firstInputValue, true)
    },
    "Url": async (args) => {
      return await this.context.queries.apiClient.setRepositoryUrl(this.taskId, args.firstInputValue)
    }
    ,
    "InviteDeveloper": async (args) => {
      const email = args.firstInputValue.trim()
      const problem = args.secondInputValue.trim()

      if (!validateEmail(email)) {
        toast.warning("Please enter a valid email")
        return
      }

      try {
        const landingContext = {
          projectKey: this.project.get().key,
          taskId: this.taskId as TID
        }

        await this.context.queries.apiClient.createInvitation(email, this.project.get().key, "Admin", "Admin", landingContext, problem)

        toast.success(`Invitation sent to ${email}`)
      } catch (e) {
        toast.error(`Failed to send invitation: ${e instanceof Error ? e.message : String(e)}`)
      }
    },
    "RequestSupport": async (args) => {
      const problem = args.firstInputValue.trim()

      if (problem.trim().length == 0) {
        toast.warning("Please provide a problem description")
        return
      }

      try {
        await this.context.queries.apiClient.inviteMatterSupport(problem, this.taskId)
        toast.success("Support request sent")
      } catch (e) {
        toast.error(`Failed to send support request: ${e instanceof Error ? e.message : String(e)}`)
      }
    },
    "None": async () => { }
  }

  private listenForUpdates() {
    try {
      if (!this.updates) {
        this.updates = this.queries.taskUpdates(this.taskId)
      }

      this.lifetime.add(this.updates.subscribe(update => {
        switch (update.className) {
          case "TaskBranchUpdatedEvent": {
            this.scheduleUpdate("refresh")
            break
          }

          case "UsingRuntimeConfigFromRepoEvent": {
            this.invalidateProjectQuery(this.project.get().key)

            const navigateToTaskId = (update as AiAgentTaskEvent.UsingRuntimeConfigFromRepoEvent).navigateToTaskId
            const location = navigateToTaskId
              ? Navigator.task(this.context.orgId, this.project.get().key, navigateToTaskId)
              : Navigator.newTask(this.context.orgId, this.project.get().key, { closeLocation: Navigator.project(this.context.orgId, this.project.get().key) })
            void this.router.navigate(location)
            break
          }

          case "GuidelinesUpdatedEvent": {
            this.scheduleUpdate("update-guidelines")
            break
          }

          case "TaskStateEvent": {
            const taskStateEvent = update as AiAgentTaskEvent.TaskStateEvent
            this.consumeTask(taskStateEvent.task)
            this.scheduleUpdate("update-task-state")
            break
          }

          case "ScreenshotRequestedEvent": {
            const screenshotEvent = update as AiAgentTaskEvent.AgentRequest.ScreenshotRequestedEvent
            void this.handleScreenshotRequest(screenshotEvent.requestId)
            break
          }

          case "ConsoleLogRequestedEvent": {
            const consoleLogEvent = update as AiAgentTaskEvent.AgentRequest.ConsoleLogRequestedEvent
            void this.handleConsoleLogRequest(consoleLogEvent.requestId)
            break
          }
        }
      }), "TaskViewModel:updates")
    } catch (e) {
      log.error("Failed to listen for updates:", e)
    }
  }

  private async handleScreenshotRequest(requestId: string) {
    try {
      const currentPreviewId = this.currentPreviewId.get()
      if (!currentPreviewId) {
        log.error("No preview available for screenshot request")
        return
      }

      const currentTabModel = this.previewTabModels.get(currentPreviewId)

      const screenshot = await currentTabModel?.client?.takeScreenshot() ?? null
      if (!screenshot) {
        log.error("Failed to take preview screenshot for agent request")
        return
      }

      const response = await fetch(screenshot)
      if (!response.ok) {
        log.error(`Failed to fetch screenshot: ${response.statusText}`)
        return
      }

      const blob = await response.blob()
      const file = new File([blob], "screenshot.png", { type: "image/png" })
      const attachment = await this.queries.apiClient.uploadFile(file)

      const currentUrl = removeTokenParams(currentTabModel?.currentUrl.get() ?? "")

      await this.queries.apiClient.provideScreenshotToolResponse(this.taskId, requestId, attachment.id, currentUrl)
      log.debug("Screenshot provided to agent", { requestId })
    } catch (error) {
      log.error("Failed to handle screenshot request:", error)
    }
  }

  private async handleConsoleLogRequest(requestId: string) {
    try {
      const currentPreviewId = this.currentPreviewId.get()
      if (!currentPreviewId) {
        log.error("No preview available for console log request")
        return
      }

      const currentTabModel = this.previewTabModels.get(currentPreviewId)
      const consoleLogs = await currentTabModel?.client?.getConsoleLogs() ?? []

      // Extract matter_token from URL to redact it from logs
      const rawUrl = currentTabModel?.currentUrl.get() ?? ""
      let matterToken: string | null = null
      try {
        const url = new URL(rawUrl)
        matterToken = url.searchParams.get("matter_token")
      } catch {
        // Ignore URL parsing errors
      }

      let formattedLogs = consoleLogs
        .map(entry => `[${entry.level.toUpperCase()}] ${new Date(entry.timestamp).toISOString()} ${entry.message}`)
        .join("\n")

      // Redact matter_token from logs
      if (matterToken) {
        formattedLogs = formattedLogs.replaceAll(matterToken, "***")
      }

      const currentUrl = removeTokenParams(rawUrl)

      await this.queries.apiClient.provideConsoleLogToolResponse(this.taskId, requestId, formattedLogs, currentUrl)
      log.debug("Console logs provided to agent", { requestId, logCount: consoleLogs.length })
    } catch (error) {
      log.error("Failed to handle console log request:", error)
    }
  }

  private async startQueueProcessing(withLoader = true) {
    if (this.isProcessing) {
      return
    }

    this.isProcessing = true
    while (this.updateQueue.length > 0) {
      try {
        const request = this.updateQueue.shift()!
        log.debug("Process event", request)
        switch (request) {
          case "refresh": {
            await this.refreshTask(withLoader)
            break
          }
          case "update-task-state": {
            await this.updateTaskState()
            break
          }
          case "update-guidelines": {
            runInAction(() => this.guidelinesReloadSignal.set(this.guidelinesReloadSignal.get() + 1))
          }
        }
      } catch (ex) {
        log.error("Error during queue processing:", ex)
      }
    }

    this.isProcessing = false
  }

  private scheduleUpdate(update: TaskViewUpdateRequest) {
    for (const existingUpdate of this.updateQueue) {
      if (existingUpdate === update) {
        return
      }
    }

    this.updateQueue.push(update)

    if (!this.isProcessing) {
      void this.startQueueProcessing(false)
    }
  }

  private consumeTask(task: AiAgentTask) {
    runInAction(() => {
      const prevTask = this.task.get()
      this.task.set(task)

      if (prevTask !== null && task?.projectKey?.key !== prevTask?.projectKey?.key) {
        this.invalidateTaskQuery(this.taskId)
        void this.router.navigate({
          ...Navigator.task(this.context.orgId, task.projectKey, this.taskId, { view: TaskViewId.Chat }),
          replace: true
        })
      }

      if (task.projectKey) {
        if ((task.runningProcess?.className === "VerifyBeforeCommand" || task.runningProcess?.className === "VerifyPreviewCommand") &&
          prevTask?.runningProcess?.className != task.runningProcess?.className &&
          (this.currentView.get() === TaskViewId.Chat || this.currentTab.get() != TabId.RuntimeConfigFile)
        ) {
          this.invalidateTaskQuery(this.taskId)
          void this.router.navigate(Navigator.task(this.context.orgId, this.project.get().key, task.id, {
            view: TaskViewId.Full,
            tab: TabId.RuntimeConfigFile
          }))
          return
        }
      }
    })
  }

  get changesModel() {
    if (!this.lazyChangesModel) {
      this.lazyChangesModel = new TaskChangesModel(this, this.context, this.queries)
    }

    return this.lazyChangesModel
  }

  get changesFromPreviousVersionModel() {
    if (!this.lazyChangesFromPreviousVersionModel) {
      this.lazyChangesFromPreviousVersionModel = new TaskChangesFromPreviousVersionModel(this, this.context, this.queries)
    }

    return this.lazyChangesFromPreviousVersionModel
  }

  readonly setCurrentTab = (tab: string = TabId.Preview) => {
    if (isTabId(tab)) {
      runInAction(() => this.currentTab.set(tab))
    }
  }

  readonly setCurrentView = (view: TaskViewId = TaskViewId.Full) => {
    runInAction(() => this.currentView.set(view))
  }

  readonly updateSplitPercentages = (leftPercentage: number, rightPercentage: number) => {
    runInAction(() => {
      this.chatPercentage.set(leftPercentage)
      this.tabsPercentage.set(rightPercentage)

      this.context.persistence.setItem(CHAT_PERCENTAGE_STORAGE_KEY, leftPercentage.toString())
      this.context.persistence.setItem(TABS_PERCENTAGE_STORAGE_KEY, rightPercentage.toString())
    })
  }

  readonly checkout = async (commitId: string, onCheckout?: () => void) => {
    await withGlobalLoader(this.context, this.queries.apiClient.checkoutTaskCheckpoint(this.taskId, commitId, true))
    onCheckout?.()
    await this.refreshTask(false)
  }

  readonly markAsSuccessful = async (commitId: string, newSuccessfulState: boolean, onMarkAsSuccessful?: () => void) => {
    await this.queries.apiClient.markAsSuccessful(this.taskId, commitId, newSuccessfulState)
    onMarkAsSuccessful?.()
    await this.refreshTask(false)
    this.invalidateTaskList()
  }

  readonly selectPreview = (preview: RuntimeConfig.ServicePreview) => {
    if (this.isMultiPreview.get()) {
      const selectedModel = Array.from(this.previewModelsById.values()).find(model => model.portModel.previewPort.get() == preview.previewPort)
      runInAction(() => this.currentPreviewId.set(selectedModel?.idV2?.get() || null))
    } else {
      runInAction(() => this.currentPreviewId.set(this.previewId.get() || null))
    }
  }

  readonly completePreviewRuntimeSetup = async () => {
    this.invalidateProjectQuery(this.project.get().key)

    // update project cover asynchronously, do not delay the task completion
    this.updateProjectCoverWithPreviewScreenshot()
      .catch(err => log.error("Failed to set project cover:", err))

    this.completingPreviewRuntimeSetup.set(true)
    let firstTaskId: TID | null = null
    try {
      firstTaskId = await withGlobalLoader(this.context, this.queries.apiClient.completePreviewRuntimeSetup(this.taskId))
      console.log(`First task created (id = ${firstTaskId}`)
      void this.router.navigate(
        firstTaskId
          ? Navigator.task(this.context.orgId, this.project.get().key, firstTaskId)
          : Navigator.project(this.context.orgId, this.project.get().key)
      )
    } finally {
      if (!firstTaskId) {
        this.completingPreviewRuntimeSetup.set(false)
      }
    }
  }

  readonly navigateFromCompletedOnboarding = () => {
    void this.router.navigate(Navigator.newTask(this.context.orgId, this.project.get().key, {
      onboarding: true,
      closeLocation: Navigator.project(this.context.orgId, this.project.get().key)
    }))
  }

  readonly updateTaskTitle = async (title: string, undoable = true, onUpdateTaskTitle?: () => void) => {
    const prevTitle = this.task.get()?.title
    await withGlobalLoader(this.context, this.queries.apiClient.updateTaskTitle(this.taskId, title))
    if (undoable) {
      toast.info("The idea was renamed", {
        action: {
          label: "Undo",
          onClick: () => {
            if (prevTitle) {
              void this.updateTaskTitle(prevTitle, false)
            }
          }
        }
      })
    }
    onUpdateTaskTitle?.()
    await this.refreshTask(false)
    this.invalidateTaskList()
  }

  readonly deleteTask = async () => {
    await withGlobalLoader(this.context, this.queries.apiClient.deleteTask(this.taskId))
    this.invalidateTaskList()
  }

  get repositoryName() {
    return this.project.get().repository.name
  }

  get taskLink() {
    const taskHref = this.router.buildLocation(Navigator.task(this.orgId, this.project.get().key, this.taskId)).href
    return new URL(taskHref, window.location.origin).href
  }

  // When duplicating a task, we use the changes from the original task as context
  readonly duplicateTask = async (task: AiAgentTask, currentCommit: string, changedFiles: DiffFile[]) => {
    if (!task.resultBranch) throw new Error("Task has no result branch")

    const contextItems: AiAgentTaskContextItem[] = changedFiles
      .filter(diffFile => diffFile.file && diffFile.file.path)
      .map(diffFile => ({
        name: diffFile.file.path,
        type: diffFile.file.new?.type === "DIR" ? "DIR" : "FILE"
      } as AiAgentTaskContextItem))

    const summary = generateStructuredSummary(task, this)
    const prompt = summary === ""
      ? "Stand by for further instructions"
      : `Here's the summary of an earlier task:\n\n${summary}\n\nStand by for further instructions.`

    const clonedTask = await withGlobalLoader(this.context, this.queries.apiClient.createTask(
      this.project.get().key,
      this.repositoryName,
      {
        prompt,
        contextItems,
        initialBranchInfo: { head: task.resultBranch, ref: currentCommit },
        settings: this.taskSettings.get(),
      }
    ))

    this.invalidateTaskList()

    return clonedTask
  }

  private async getCurrentPrincipal() {
    try {
      const principal = await withGlobalLoader(this.context, this.queries.me())
      runInAction(() => this.me.set(principal))
    } catch (e) {
      log.error("Failed to get current principal:", e)
    }
  }

  private async refreshTask(withLoader = true) {
    if (withLoader) {
      runInAction(() => {
        this.context.isLoading.set(true)
        this.isLoading.set(true)
      })
    }

    try {
      this.invalidateTaskQuery(this.taskId)

      const taskWithDetails = await this.queries.taskDetails(this.taskId)

      await this.updateTask(taskWithDetails, this.currentTab.get() === "changes")

      if (withLoader) {
        runInAction(() => {
          this.context.isLoading.set(false)
          this.isLoading.set(false)
        })
      }
    } catch (err) {
      log.error("Failed to fetch logs:", err)
    }
  }

  private async updateTaskState() {
    this.invalidateTaskQuery(this.taskId)

    const updatedTask = await this.queries.task(this.taskId)
    this.consumeTask(updatedTask)
  }

  private async updateTask(taskWithDetails: AiAgentTaskDetails, loadDiff: boolean = false) {
    runInAction(() => {
      console.log(`TaskViewModel:updateTask - updating task with details: ${JSON.stringify(taskWithDetails)}`)

      this.task.set(taskWithDetails.task)
      this.previewId.set(taskWithDetails.previewId ?? false)
      this.previewV2Ids.set(taskWithDetails.previewV2Ids ?? false)
      this.previewV2EnvId.set(taskWithDetails.previewV2EnvId ?? false)
      this.runtimeConfig.set(taskWithDetails.runtimeConfig ?? null)
      this.historyItems.set(taskWithDetails.history)
    })

    await this.changesModel.loadChanges(loadDiff)
    await this.changesFromPreviousVersionModel.loadChanges(loadDiff)
  }

  private async subscribeToTaskStats() {
    const task = this.task.get()
    if (!task) return
    if (!this.devMode) return

    try {
      const subscription = await this.queries.getTaskStats(task.id)
      runInAction(() => this.stats.set(subscription.initial))

      const channel = subscription.channel
      this.lifetime.add(channel.subscribe(stats => {
        runInAction(() => this.stats.set(stats))
      }), "TaskViewModel:subscribeToTaskStats")
    } catch (e) {
      log.error("Failed to subscribe to task stats:", e)
    }
  }

  private getPermissions(): ProjectPermissionsProp {
    if (this.permissions == null) {
      this.permissions = projectPermissionsProp(this.context.queries, this.project.get().key)
    }
    return this.permissions
  }

  get canEditProject() {
    return this.getPermissions().canEditProject
  }

  private async subscribeToRepoSyncStatus() {
    try {
      const subscription = await this.context.queries.subscribeToRepositorySyncErrors(this.project.get().key, this.project.get().repository.name)
      runInAction(() => {
        this.repoSyncStatus.set(subscription.initial)
      })

      const channel = subscription.channel
      this.lifetime.add(channel.subscribe(status => {
        runInAction(() => this.repoSyncStatus.set(status))
      }), "TaskViewModel:subscribeToRepoSyncStatus")
    } catch (e) {
      log.error("Failed to subscribe to repo sync status:", e)
    }
  }

  private invalidateProjectQuery(projectKey: ProjectKey) {
    void this.context.queryClient.invalidateQueries(projectQueryKey(projectKey))
  }

  invalidateTaskQuery(taskId: string) {
    void this.context.queryClient.invalidateQueries(taskKey(taskId))
    void this.context.queryClient.invalidateQueries(taskDetailsKey(taskId))
  }
}

export const TaskViewModelContext = createContext<TaskViewModel | null>(null)

type TaskViewUpdateRequest = "refresh" | "update-task-state" | "update-guidelines"

export function versionTitle(number: number): string {
  return number === 0 ? "Initial version" : `Version ${number}`
}

export type TaskContextLoadModel = {
  contextLoad: IObservableValue<number | null>
  currentContextSize: IObservableValue<number>
  totalContextLimit: IObservableValue<number | null>
}
